import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, functionsUsEast1 } from '../../../utils/init-firebase';

/**
 * API utility functions for Rocks feature
 * Handles calls to Firebase Cloud Functions that replace Supabase Edge Functions
 * 
 * Note: Firebase Cloud Functions automatically handle authentication via the Firebase SDK.
 * The user's authentication token is automatically included in the request.
 */

/**
 * Evaluate a rock using AI
 * Calls the Firebase Cloud Function 'evaluateRock' to analyze and score a rock
 * 
 * @param rockId - The ID of the rock to evaluate
 * @returns Promise resolving to the evaluation result object
 * @throws Error if the function call fails or returns an error
 */
export async function evaluateRock(rockId: string): Promise<any> {
  if (!rockId) {
    throw new Error('Rock ID is required to evaluate a rock');
  }

  try {
    // Use functionsUsEast1 since evaluateRock is deployed in us-east1
    const evaluateRockFunction = httpsCallable(functionsUsEast1, 'evaluateRock');
    const result = await evaluateRockFunction({ rockId });
    
    // Check if the function returned an error in the data
    if (result.data && typeof result.data === 'object' && 'error' in result.data) {
      throw new Error((result.data as any).error || 'Failed to evaluate rock');
    }
    
    return result.data; // This will be the full evaluation object
  } catch (error: any) {
    console.error('Error evaluating rock:', error);
    
    // Provide more specific error messages
    if (error?.code === 'functions/not-found') {
      throw new Error('Rock evaluation function is not available. Please contact support.');
    } else if (error?.code === 'functions/internal' || error?.code === 'functions/unknown') {
      throw new Error('An internal error occurred while evaluating the rock. Please try again.');
    } else if (error?.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to evaluate rocks. Please contact support.');
    } else if (error?.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to evaluate rock. Please try again.');
    }
  }
}

/**
 * Check if user has accepted AI terms
 * Calls the Firebase Cloud Function 'checkAITerms' to verify terms acceptance
 * 
 * @param userId - The ID of the user to check
 * @returns Promise resolving to true if terms are accepted, false otherwise
 */
export async function checkAITerms(userId: string): Promise<boolean> {
  if (!userId) {
    return false;
  }

  try {
    // Use functionsUsEast1 since checkAITerms is deployed in us-east1
    const checkAITermsFunction = httpsCallable(functionsUsEast1, 'checkAITerms');
    const result = await checkAITermsFunction({ userId });
    return (result.data as { accepted: boolean }).accepted;
  } catch (error: any) {
    console.error('Error checking AI terms:', error);
    // If function doesn't exist or is not deployed, return false (user hasn't accepted)
    if (error?.code === 'functions/not-found' || error?.code === 'functions/internal') {
      console.warn('AI terms check function not available. Assuming terms not accepted.');
      return false;
    }
    return false;
  }
}

/**
 * Accept AI terms for the current user
 * Calls the Firebase Cloud Function 'acceptAITerms' to record terms acceptance
 * 
 * @param userId - The ID of the user accepting the terms
 * @returns Promise resolving to true if successful
 * @throws Error if the function call fails
 */
export async function acceptAITerms(userId: string): Promise<boolean> {
  if (!userId) {
    throw new Error('User ID is required to accept AI terms');
  }

  try {
    // Use functionsUsEast1 since acceptAITerms is deployed in us-east1
    const acceptAITermsFunction = httpsCallable(functionsUsEast1, 'acceptAITerms');
    const result = await acceptAITermsFunction({ userId });
    
    // Check if the function returned an error in the data
    if (result.data && typeof result.data === 'object' && 'error' in result.data) {
      throw new Error((result.data as any).error || 'Failed to accept AI terms');
    }
    
    return true;
  } catch (error: any) {
    console.error('Error accepting AI terms:', error);
    
    // Provide more specific error messages
    if (error?.code === 'functions/not-found') {
      throw new Error('AI terms acceptance function is not available. Please contact support.');
    } else if (error?.code === 'functions/internal' || error?.code === 'functions/unknown') {
      throw new Error('An internal error occurred while accepting AI terms. Please try again.');
    } else if (error?.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to accept AI terms. Please contact support.');
    } else if (error?.message) {
      throw new Error(error.message);
    } else {
      throw new Error('Failed to accept AI terms. Please try again.');
    }
  }
}

